<template>
  <div :class="'flex flex-col ' + props.size">
    <slot name="label">
      <label :for="props.name" :class="props.classes">{{ label }}</label>
    </slot>
    <slot>
      <InputGroup>
        <AutoComplete
          :name="props.name"
          :id="props.name"
          v-model="entity"
          class="w-full"
          input-class="p-2 w-full h-10 rounded-r-none"
          forceSelection
          :optionLabel="getEntityDisplay"
          :suggestions="filteredLookupItems"
          @complete="search"
          @click.stop
        />
        <IconButton class="w-12" icon="search" outlined @click="toggleEntityList(true)" @click.stop></IconButton>
        <IconButton class="w-12" icon="plus" outlined @click="toggleMaintainEntity('Add')" @click.stop></IconButton>
      </InputGroup>
    </slot>
  </div>

  <TSDialog
    v-if="maintainEntityMode"
    v-model:visible="displayMaintainEntity"
    :label="maintainEntityMode + ' Entity'"
    icon="user-plus"
    v-on:after-hide="toggleMaintainEntity(undefined)"
    size="w-[1000px]"
  >
    <template #content>
      <MaintainEntity :mode="maintainEntityMode" :entity="undefined" @submitted="entityCreated" />
    </template>
  </TSDialog>
  <TSDialog v-model:visible="displayEntityList" label="Entity Lookup" icon="user" size="w-[1000px]">
    <template #content>
      <EntityLookupListView :lookup-items="lookupItems" @entity-selected="entitySelected"></EntityLookupListView>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { type AutoCompleteCompleteEvent } from "primevue/autocomplete";
import type { Entity } from "@/types/Entity";
import { getEntityDisplay } from "@/services/Utility";

const filteredLookupItems = ref<Entity[]>();

const entity = defineModel<Entity | null>("entity");
const lookupItems = defineModel<Entity[]>("lookupItems", { required: true });

interface Props {
  name: string;
  value?: string;
  label: string;
  size?: string;
  classes?: string;
  autofocus?: boolean;
  defaultTBD?: boolean;
  tbdEntity?: Entity;
}
const props = withDefaults(defineProps<Props>(), {
  size: "w-full",
  classes: "font-bold",
  autofocus: false,
  defaultTBD: false,
});

//TODO: This shouldn't have to be a separate component but couldn't figure out how to compine with one meant for forms
const maintainEntityMode = ref<"Add" | "Edit" | "Copy">();
const displayMaintainEntity = ref(false);
const entityListActive = ref(false);
const displayEntityList = ref(false);

const toggleMaintainEntity = (value: "Add" | "Edit" | "Copy" | undefined): void => {
  maintainEntityMode.value = value;
  displayMaintainEntity.value = value != null;
};

const toggleEntityList = (value: boolean): void => {
  entityListActive.value = value;
  displayEntityList.value = value;
};

const search = (event: AutoCompleteCompleteEvent): void => {
  setTimeout(() => {
    if (!event.query.trim().length) {
      filteredLookupItems.value = [...lookupItems.value];
    } else {
      const searchTerm = event.query.toLowerCase();
      filteredLookupItems.value = lookupItems.value.filter((entity: Entity) => {
        const displayMatch = getEntityDisplay(entity).toLowerCase().startsWith(searchTerm);
        const firstNameMatch = entity.firstName.toLowerCase().startsWith(searchTerm);
        const lastNameMatch = entity.lastName.toLowerCase().startsWith(searchTerm);
        return displayMatch || firstNameMatch || lastNameMatch;
      });
    }
  }, 250);
};

const entitySelected = (e: Entity): void => {
  toggleEntityList(false);
  entity.value = e;
};

const entityCreated = (newEntity: Entity | null): void => {
  if (newEntity == null) return;
  lookupItems.value.push(newEntity);
  entity.value = newEntity;
  toggleMaintainEntity(undefined);
};

if (props.defaultTBD) {
  watch(
    () => entity.value,
    (e: Entity | null | undefined) => {
      if (e == null) entity.value = props.tbdEntity;
    }
  );
}
</script>
